import { call, put, takeLatest } from 'redux-saga/effects';
import {
  FETCH_SENGAGE_DATA_INIT,
  FETCH_SENGAGE_TIMELINE_DATA_INIT,
  FETCH_SENGAGE_CAMPAIGN_FRAMES_INIT,
  FETCH_TIMING_PATTERNS_INIT,
  FETCH_OUTLET_RANKING_INIT,
  FETCH_PROMO_MECHANICS_INIT,
} from './constants';
import {
  fetchSmartEngageSuccess,
  fetchSmartEngageFailure,
  fetchSmartEngageTimelineSuccess,
  fetchSmartEngageTimelineFailure,
  fetchSmartEngageCampaignFramesSuccess,
  fetchSmartEngageCampaignFramesFailure,
  fetchTimingPatternsSuccess,
  fetchTimingPatternsFailure,
  fetchOutletRankingSuccess,
  fetchOutletRankingFailure,
  fetchPromoMechanicsSuccess,
  fetchPromoMechanicsFailure,
} from './actions';
import {
  fetchSmartEngageData as fetchSmartEngageDataAPI,
  fetchSmartEngageTimelineData as fetchSmartEngageTimelineDataAPI,
  fetchSmartEngageCampaignFrames as fetchSmartEngageCampaignFramesAPI,
  fetchTimingPatternsData as fetchTimingPatternsDataAPI,
  fetchOutletRankingData as fetchOutletRankingDataAPI,
  fetchPromoMechanicsData as fetchPromoMechanicsDataAPI,
} from './service';
import { getMerchantAndDate } from 'redux/rootSaga';
import { formatSmartEngageData, normalizeCampaignFramesResponse, normalizeTimingPatternsResponse, normalizePromoMechanicsResponse, normalizeOutletRankingResponse } from './formatters';

function* fetchSengageDataSaga() {
  try {
    const { merchantIdList, startDate, endDate } = yield getMerchantAndDate();
    const chainId = localStorage.getItem('chain_id');
    const response = yield call(fetchSmartEngageDataAPI, { merchant_id: chainId ? [chainId] : merchantIdList, start_date: startDate, end_date: endDate });
    const payload = response?.data;
    const formattedData = formatSmartEngageData(payload);
    yield put(fetchSmartEngageSuccess(formattedData));
  } catch (error) {
    yield put(fetchSmartEngageFailure(error));
  }
}

function* fetchSengageCampaignFramesSaga() {
  try {
    const { merchantIdList, startDate, endDate } = yield getMerchantAndDate();
    const chainId = localStorage.getItem('chain_id');
    const response = yield call(fetchSmartEngageCampaignFramesAPI, {
      merchant_id: chainId ? [chainId] : merchantIdList,
      start_date: startDate,
      end_date: endDate,
    });
    const normalized = normalizeCampaignFramesResponse(response?.data);
    yield put(fetchSmartEngageCampaignFramesSuccess(normalized));
  } catch (error) {
    yield put(fetchSmartEngageCampaignFramesFailure(error));
  }
}

// TODO: Format timeline API response through formatter when API structure changes
function* fetchSengageTimelineDataSaga(action) {
  try {
    const chainId = localStorage.getItem('chain_id');
    const { merchantIdList, startDate, endDate } = yield getMerchantAndDate();
    const response = yield call(fetchSmartEngageTimelineDataAPI, {
      merchant_id: chainId ? [chainId] : merchantIdList,
      start_date: startDate,
      end_date: endDate,
      ...action.payload,
    });
    yield put(fetchSmartEngageTimelineSuccess(response?.data));
  } catch (error) {
    yield put(fetchSmartEngageTimelineFailure(error));
  }
}

function* fetchTimingPatternsSaga() {
  try {
    const { merchantIdList, startDate, endDate } = yield getMerchantAndDate();
    const chainId = localStorage.getItem('chain_id');
    const response = yield call(fetchTimingPatternsDataAPI, {
      merchant_id: chainId ? [chainId] : merchantIdList,
      start_date: startDate,
      end_date: endDate,
    });
    const payload = response?.data;

    if (payload?.error || payload?.errors) {
      const msg =
        payload.error?.message ||
        payload.error ||
        payload.errors?.message ||
        payload.errors ||
        'API returned an error';
      yield put(fetchTimingPatternsFailure(String(msg)));
      return;
    }

    yield put(fetchTimingPatternsSuccess(normalizeTimingPatternsResponse(payload)));
  } catch (error) {
    let message;
    if (error?.response?.status) {
      message = `API failed with status code ${error.response.status}`;
    } else if (error?.request) {
      message = 'Something went wrong';
    } else {
      message = error?.message || 'Something went wrong';
    }
    yield put(fetchTimingPatternsFailure(message));
  }
}

function* fetchOutletRankingSaga() {
  try {
    const { merchantIdList, startDate, endDate } = yield getMerchantAndDate();
    const chainId = localStorage.getItem('chain_id');
    const response = yield call(fetchOutletRankingDataAPI, {
      merchant_id: chainId ? [chainId] : merchantIdList,
      start_date: startDate,
      end_date: endDate,
    });
    const payload = response?.data;

    if (payload?.error || payload?.errors) {
      const msg =
        payload.error?.message ||
        payload.error ||
        payload.errors?.message ||
        payload.errors ||
        'API returned an error';
      yield put(fetchOutletRankingFailure(String(msg)));
      return;
    }

    yield put(fetchOutletRankingSuccess(normalizeOutletRankingResponse(payload)));
  } catch (error) {
    let message;
    if (error?.response?.status) {
      message = `API failed with status code ${error.response.status}`;
    } else if (error?.request) {
      message = 'Something went wrong';
    } else {
      message = error?.message || 'Something went wrong';
    }
    yield put(fetchOutletRankingFailure(message));
  }
}

function* fetchPromoMechanicsSaga() {
  try {
    const { merchantIdList, startDate, endDate } = yield getMerchantAndDate();
    const chainId = localStorage.getItem('chain_id');
    const response = yield call(fetchPromoMechanicsDataAPI, {
      merchant_id: chainId ? [chainId] : merchantIdList,
      start_date: startDate,
      end_date: endDate,
    });
    const payload = response?.data;

    if (payload?.error || payload?.errors) {
      const msg =
        payload.error?.message ||
        payload.error ||
        payload.errors?.message ||
        payload.errors ||
        'API returned an error';
      yield put(fetchPromoMechanicsFailure(String(msg)));
      return;
    }

    yield put(fetchPromoMechanicsSuccess(normalizePromoMechanicsResponse(payload)));
  } catch (error) {
    let message;
    if (error?.response?.status) {
      message = `API failed with status code ${error.response.status}`;
    } else if (error?.request) {
      message = 'Something went wrong';
    } else {
      message = error?.message || 'Something went wrong';
    }
    yield put(fetchPromoMechanicsFailure(message));
  }
}

export default function* watchSengageData() {
  yield takeLatest(FETCH_SENGAGE_DATA_INIT, fetchSengageDataSaga);
  yield takeLatest(FETCH_SENGAGE_CAMPAIGN_FRAMES_INIT, fetchSengageCampaignFramesSaga);
  yield takeLatest(FETCH_SENGAGE_TIMELINE_DATA_INIT, fetchSengageTimelineDataSaga);
  yield takeLatest(FETCH_TIMING_PATTERNS_INIT, fetchTimingPatternsSaga);
  yield takeLatest(FETCH_OUTLET_RANKING_INIT, fetchOutletRankingSaga);
  yield takeLatest(FETCH_PROMO_MECHANICS_INIT, fetchPromoMechanicsSaga);
}